import { type Editor } from '@tiptap/react'
import {
  Bold, Italic, Code, Heading2, Heading3, List, ListOrdered, Quote, Minus,
  Link as LinkIcon, Image as ImageIcon, Table as TableIcon, BookOpen, ListCollapse, ListTree, Video, Clapperboard,
  AlignLeft, AlignCenter, AlignRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import { Tooltip } from '@/components/ui/Tooltip'
import type { DialogKind } from './dialogKinds'

interface EditorToolbarProps {
  editor: Editor | null
  onOpenDialog: (kind: DialogKind) => void
}

export function EditorToolbar({ editor, onOpenDialog }: EditorToolbarProps) {
  if (!editor) return null

  const items = [
    { icon: Bold, action: () => editor.chain().focus().toggleBold().run(), active: editor.isActive('bold'), label: 'Bold' },
    { icon: Italic, action: () => editor.chain().focus().toggleItalic().run(), active: editor.isActive('italic'), label: 'Italic' },
    { icon: Code, action: () => editor.chain().focus().toggleCode().run(), active: editor.isActive('code'), label: 'Code' },
    { icon: Heading2, action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(), active: editor.isActive('heading', { level: 2 }), label: 'Heading 2' },
    { icon: Heading3, action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(), active: editor.isActive('heading', { level: 3 }), label: 'Heading 3' },
    { icon: List, action: () => editor.chain().focus().toggleBulletList().run(), active: editor.isActive('bulletList'), label: 'Bullet list' },
    { icon: ListOrdered, action: () => editor.chain().focus().toggleOrderedList().run(), active: editor.isActive('orderedList'), label: 'Numbered list' },
    { icon: Quote, action: () => editor.chain().focus().toggleBlockquote().run(), active: editor.isActive('blockquote'), label: 'Quote' },
    { icon: AlignLeft, action: () => editor.chain().focus().setTextAlign('left').run(), active: editor.isActive({ textAlign: 'left' }), label: 'Align left' },
    { icon: AlignCenter, action: () => editor.chain().focus().setTextAlign('center').run(), active: editor.isActive({ textAlign: 'center' }), label: 'Align center' },
    { icon: AlignRight, action: () => editor.chain().focus().setTextAlign('right').run(), active: editor.isActive({ textAlign: 'right' }), label: 'Align right' },
    { icon: Minus, action: () => editor.chain().focus().setHorizontalRule().run(), active: false, label: 'Divider' },
    { icon: ListCollapse, action: () => editor.chain().focus().setDetails().run(), active: editor.isActive('details'), label: 'Collapsible section' },
    { icon: ListTree, action: () => editor.chain().focus().insertTableOfContents().run(), active: editor.isActive('tableOfContents'), label: 'Table of contents' },
    { icon: LinkIcon, action: () => onOpenDialog('link'), active: editor.isActive('link'), label: 'Link' },
    { icon: BookOpen, action: () => onOpenDialog('article'), active: false, label: 'Link to article' },
    { icon: ImageIcon, action: () => onOpenDialog('image'), active: editor.isActive('image'), label: editor.isActive('image') ? 'Edit image' : 'Insert image' },
    { icon: Video, action: () => onOpenDialog('video'), active: editor.isActive('youtube'), label: 'Embed video' },
    { icon: Clapperboard, action: () => onOpenDialog('loom'), active: editor.isActive('loomVideo'), label: 'Embed Loom video' },
    {
      icon: TableIcon,
      action: () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
      active: editor.isActive('table'),
      label: 'Insert table',
    },
  ]

  const tableOps = [
    { label: '+ Row', action: () => editor.chain().focus().addRowAfter().run() },
    { label: '+ Col', action: () => editor.chain().focus().addColumnAfter().run() },
    { label: '− Row', action: () => editor.chain().focus().deleteRow().run() },
    { label: '− Col', action: () => editor.chain().focus().deleteColumn().run() },
    { label: 'Header row', action: () => editor.chain().focus().toggleHeaderRow().run() },
    { label: 'Delete table', action: () => editor.chain().focus().deleteTable().run() },
  ]

  return (
    <>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border p-1.5">
        {items.map(({ icon: Icon, action, active, label }) => (
          <Tooltip key={label} content={label} side="bottom">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn('size-8', active && 'bg-muted text-foreground')}
              onClick={action}
              aria-label={label}
            >
              <Icon className="size-4" />
            </Button>
          </Tooltip>
        ))}
      </div>
      {editor.isActive('table') && (
        <div className="flex flex-wrap items-center gap-1 border-b border-border bg-muted/30 px-1.5 py-1">
          {tableOps.map(({ label, action }) => (
            <Button key={label} type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={action}>
              {label}
            </Button>
          ))}
        </div>
      )}
    </>
  )
}
